/**
 * BLANC SPACE CUSTOM: wishlist.
 *
 * Persistence is a two-layer store: localStorage is the synchronous,
 * optimistic cache (hearts respond instantly, works offline / with the app
 * disabled), and the Swish SDK (window.WishlistKing, loaded headless by the
 * "App Settings" app embed) syncs behind it — guest lists live server-side per
 * browser session and merge into the customer's account on sign-in. Swish is
 * authoritative once its first snapshot arrives; in-flight local intents are
 * tracked so slow networks can't resurrect removed items. Every component
 * consumes the store solely through list/has/add/remove/clear and the
 * `blancspace:wishlist-change` document event.
 */

const STORAGE_KEY = 'blancspace.wishlist';
const CHANGE_EVENT = 'blancspace:wishlist-change';
const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

const HEART_PATH =
  'M11 5.49705C6.6512 0.161847 2.06048 3.95337 2 8.04009C2 14.1212 9.2792 19.1151 11 19.1151C12.7208 19.1151 20 14.1212 20 8.03865C19.9395 3.95193 15.3488 0.161847 11 5.49705Z';

const createLocalStore = () => {
  const read = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((item) => item && typeof item.handle === 'string') : [];
    } catch (error) {
      return [];
    }
  };

  const persist = (items) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (error) {
      // The storefront remains usable when storage is unavailable.
    }
  };

  return {
    list: () => read(),
    has: (handle) => read().some((item) => item.handle === handle),
    add(entry) {
      const items = read();
      if (items.some((item) => item.handle === entry.handle)) return;
      items.unshift({ ...entry, addedAt: Date.now() });
      persist(items);
    },
    remove(handle) {
      persist(read().filter((item) => item.handle !== handle));
    },
    clear() {
      persist([]);
    },
  };
};

const createSwishSyncedStore = () => {
  const local = createLocalStore();
  const swishItemIds = new Map(); // productHandle -> Swish wishlist item id
  const desired = new Map(); // productHandle -> 'saved' | 'removed' (in-flight intents)
  const inflight = new Set();
  let synced = false; // first Swish snapshot reconciled (this page load)

  /* The one-time localStorage->Swish seed must run once per BROWSER, not once
     per page load: a browser that already synced holds a mirror, and re-seeding
     from it resurrects items the shopper removed on another device. */
  const SEEDED_KEY = 'blancspace.wishlist.seeded';
  const hasSeeded = () => {
    try { return localStorage.getItem(SEEDED_KEY) === '1'; } catch (error) { return true; }
  };
  const markSeeded = () => {
    try { localStorage.setItem(SEEDED_KEY, '1'); } catch (error) { /* storage unavailable */ }
  };

  /* Removal intents survive navigation: a remove followed by an immediate page
     change used to lose its in-flight intent, and the next page's reconcile
     re-added the item from the stale Swish snapshot. */
  const PENDING_REMOVALS_KEY = 'blancspace.wishlist.removals';
  const readPendingRemovals = () => {
    try {
      const parsed = JSON.parse(localStorage.getItem(PENDING_REMOVALS_KEY) || '[]');
      return Array.isArray(parsed) ? parsed.filter((handle) => typeof handle === 'string') : [];
    } catch (error) { return []; }
  };
  const writePendingRemovals = (handles) => {
    try { localStorage.setItem(PENDING_REMOVALS_KEY, JSON.stringify(handles)); } catch (error) { /* storage unavailable */ }
  };
  const addPendingRemoval = (handle) => {
    const pending = readPendingRemovals();
    if (!pending.includes(handle)) writePendingRemovals(pending.concat(handle));
  };
  const clearPendingRemoval = (handle) => {
    writePendingRemovals(readPendingRemovals().filter((item) => item !== handle));
  };
  readPendingRemovals().forEach((handle) => desired.set(handle, 'removed'));

  const whenSwish = new Promise((resolve) => {
    if (window.WishlistKing) return resolve(window.WishlistKing);
    let waited = 0;
    const timer = setInterval(() => {
      waited += 300;
      if (window.WishlistKing) {
        clearInterval(timer);
        resolve(window.WishlistKing);
      } else if (waited >= 20000) {
        // Embed disabled or blocked: stay a pure localStorage wishlist.
        clearInterval(timer);
        resolve(null);
      }
    }, 300);
  });

  const flush = (wk) => {
    desired.forEach((want, handle) => {
      if (inflight.has(handle)) return;
      const itemId = swishItemIds.get(handle);

      if (want === 'saved' && !itemId) {
        inflight.add(handle);
        wk.addWishlistItem({ productHandle: handle })
          .then((result) => {
            const item = result && result.wishlistItem;
            if (item && item.id) swishItemIds.set(handle, item.id);
            if (desired.get(handle) === 'saved') desired.delete(handle);
          })
          .catch(() => {}) // retried on the next flush/reconcile
          .finally(() => inflight.delete(handle));
      } else if (want === 'saved' && itemId) {
        desired.delete(handle);
      } else if (want === 'removed' && itemId) {
        inflight.add(handle);
        wk.removeWishlistItem({ wishlistItemId: itemId })
          .then(() => {
            swishItemIds.delete(handle);
            if (desired.get(handle) === 'removed') desired.delete(handle);
            clearPendingRemoval(handle);
          })
          .catch(() => {})
          .finally(() => inflight.delete(handle));
      } else if (want === 'removed' && !itemId && synced) {
        // Swish never had it (or it's already gone) — intent satisfied.
        desired.delete(handle);
        clearPendingRemoval(handle);
      }
    });
  };

  const reconcile = (wk, wishlist) => {
    const items = (wishlist && wishlist.items) || [];
    swishItemIds.clear();
    items.forEach((item) => {
      const handle = (item.product && item.product.handle) || item.productHandle || '';
      if (handle) swishItemIds.set(handle, item.id);
    });

    if (!synced) {
      // One-time seed: anything saved before Swish first synced in THIS
      // browser migrates up rather than being clobbered by the first snapshot.
      // A browser that has synced before holds a mirror, not an original —
      // seeding from it would resurrect removals made on other devices.
      if (!hasSeeded()) {
        local.list().forEach((entry) => {
          if (!swishItemIds.has(entry.handle) && desired.get(entry.handle) !== 'removed') {
            desired.set(entry.handle, 'saved');
          }
        });
        markSeeded();
      }
      synced = true;
    }

    // Swish is authoritative for anything without an in-flight local intent.
    let changed = false;
    items.forEach((item) => {
      const handle = (item.product && item.product.handle) || item.productHandle || '';
      if (!handle) return;
      if (!local.has(handle) && desired.get(handle) !== 'removed') {
        local.add({ handle, id: (item.product && item.product.id) || null, title: '' });
        changed = true;
      }
    });
    local.list().forEach((entry) => {
      if (!swishItemIds.has(entry.handle) && !desired.has(entry.handle)) {
        local.remove(entry.handle);
        changed = true;
      }
    });

    flush(wk);
    if (changed) broadcast();
  };

  whenSwish.then((wk) => {
    if (!wk) return;
    try {
      wk._state.observeWishlist({ wishlistId: 'mine' }).subscribe((wishlist) => reconcile(wk, wishlist));
    } catch (error) {
      // SDK internals moved: local wishlist keeps working; sync resumes on fix.
    }
  });

  return {
    list: () => local.list(),
    has: (handle) => local.has(handle),
    add(entry) {
      local.add(entry);
      desired.set(entry.handle, 'saved');
      clearPendingRemoval(entry.handle);
      whenSwish.then((wk) => wk && flush(wk));
    },
    remove(handle) {
      local.remove(handle);
      desired.set(handle, 'removed');
      addPendingRemoval(handle);
      whenSwish.then((wk) => wk && flush(wk));
    },
    clear() {
      local.clear();
      // clearWishlist is not a verified Swish SDK method; remove items
      // individually through the proven per-item API instead.
      swishItemIds.forEach((itemId, handle) => {
        desired.set(handle, 'removed');
        addPendingRemoval(handle);
      });
      whenSwish.then((wk) => wk && flush(wk));
    },
  };
};

const store = createSwishSyncedStore();

const broadcast = () => {
  const items = store.list();
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { bubbles: true, detail: { items, count: items.length } }));
};

const toggle = (entry) => {
  const saved = store.has(entry.handle);
  if (saved) store.remove(entry.handle);
  else store.add(entry);
  broadcast();
  return !saved;
};

// Cross-tab sync: another tab mutating the list refreshes this tab's hearts and badge.
window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY) broadcast();
});

const formatMoney = (cents) => {
  const template =
    (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) ||
    '£{{amount}}';
  const amount = (cents / 100).toFixed(2);
  return template.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) =>
    key.startsWith('amount_no_decimals') ? String(Math.round(cents / 100)) : amount
  );
};

class WishlistToggle extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    this._button = this.querySelector('button');
    this._button?.addEventListener(
      'click',
      () => {
        const added = toggle({
          handle: this.getAttribute('handle') || '',
          id: Number(this.getAttribute('product-id')) || null,
          title: this.getAttribute('product-title') || '',
        });
        if (added) {
          this.classList.add('is-popping');
          this.addEventListener('animationend', () => this.classList.remove('is-popping'), { once: true });
        }
      },
      { signal }
    );

    document.addEventListener(CHANGE_EVENT, () => this._sync(), { signal });
    this._sync();
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  _sync() {
    const saved = store.has(this.getAttribute('handle') || '');
    this.classList.toggle('is-saved', saved);
    this._button?.setAttribute('aria-pressed', saved ? 'true' : 'false');
  }
}

class WishlistCount extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    document.addEventListener(CHANGE_EVENT, (event) => this._render(event.detail.count), { signal });
    window.addEventListener('pageshow', () => this._render(store.list().length), { signal });
    this._render(store.list().length);
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  _render(count) {
    this.textContent = String(count);
    this.classList.toggle('opacity-0', count === 0);
  }
}

// Shared across instances (page grid + drawer grid) and across drawer
// reconnects — the theme's Drawer moves itself to <body> when it opens.
const productCache = new Map();

class WishlistGrid extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const { signal } = this._abortController;

    document.addEventListener(CHANGE_EVENT, () => this._render(), { signal });

    // `defer-render` grids (the header drawer) skip fetching product data on
    // page load and first render when their drawer opens.
    this._activated = !this.hasAttribute('defer-render');
    if (!this._activated) {
      const drawer = this.closest('.drawer');
      drawer?.addEventListener(
        'dialog:after-show',
        () => {
          this._activated = true;
          this._render();
        },
        { signal }
      );
      if (drawer?.hasAttribute('open')) this._activated = true;
    }

    this._render();
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  async _render() {
    if (!this._activated) return;
    const grid = this.querySelector('[data-wishlist-grid]');
    const empty = this.querySelector('[data-wishlist-empty]');
    const loading = this.querySelector('[data-wishlist-loading]');
    // The drawer's footer link is slotted outside this element.
    const footer = (this.closest('.drawer') || this).querySelector('[data-wishlist-footer]');
    if (!grid || !empty) return;

    const items = store.list();
    if (footer) footer.hidden = items.length === 0;

    if (items.length === 0) {
      grid.hidden = true;
      if (loading) loading.hidden = true;
      empty.hidden = false;
      return;
    }

    empty.hidden = true;
    if (loading && grid.hidden) loading.hidden = false;

    const renderToken = (this._renderToken = Symbol());
    const products = await Promise.all(items.map((item) => this._fetchProduct(item.handle)));
    if (renderToken !== this._renderToken) return;

    // Products deleted or unpublished since being saved are pruned quietly.
    const missing = items.filter((item, index) => products[index] === null);
    missing.forEach((item) => store.remove(item.handle));

    const cards = products.filter(Boolean).map((product) => this._renderCard(product));

    grid.replaceChildren(...cards);
    grid.hidden = false;
    if (loading) loading.hidden = true;
    if (missing.length > 0) broadcast();
  }

  async _fetchProduct(handle) {
    if (productCache.has(handle)) return productCache.get(handle);

    let product = null;
    try {
      const response = await fetch(`${ROOT}products/${handle}.js`);
      if (response.ok) {
        product = await response.json();
      } else if (response.status !== 404 && response.status !== 410) {
        // Throttling (429) or server errors must not read as "product deleted":
        // keep the item saved and skip rendering it this pass, uncached.
        return undefined;
      }
    } catch (error) {
      // Network failure: keep the item saved and simply skip rendering it this pass.
      return undefined;
    }

    productCache.set(handle, product);
    return product;
  }

  _renderCard(product) {
    const surface = this.getAttribute('surface') === 'soft_grey' ? 'soft_grey' : 'white';
    const card = document.createElement('div');
    card.className = `bs-collection-card bs-collection-card--media-${surface} bs-wishlist__card`;

    const media = document.createElement('div');
    media.className = 'bs-collection-card__media product-card__figure';

    const mediaLink = document.createElement('a');
    mediaLink.className = 'bs-collection-card__media-link';
    mediaLink.href = product.url;
    mediaLink.setAttribute('aria-label', product.title);

    if (product.featured_image) {
      const image = document.createElement('img');
      image.className = 'product-card__image product-card__image--primary';
      image.src = `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=600`;
      image.alt = product.title;
      image.loading = 'lazy';
      mediaLink.appendChild(image);
    }

    media.appendChild(mediaLink);
    media.appendChild(this._renderToggle(product));
    card.appendChild(media);

    const info = document.createElement('div');
    info.className = 'bs-collection-card__info product-card__info';

    const title = document.createElement('a');
    title.className = 'bs-collection-card__title bold';
    title.href = product.url;
    title.textContent = product.title;
    info.appendChild(title);

    const price = document.createElement('div');
    price.className = 'bs-collection-card__price';

    const onSale = product.compare_at_price && product.compare_at_price > product.price;
    const salePrice = document.createElement('span');
    salePrice.className = onSale ? 'bs-wishlist__price bs-wishlist__price--sale' : 'bs-wishlist__price';
    salePrice.textContent = formatMoney(product.price);
    price.appendChild(salePrice);

    if (onSale) {
      const comparePrice = document.createElement('s');
      comparePrice.className = 'bs-wishlist__price-compare';
      comparePrice.textContent = formatMoney(product.compare_at_price);
      price.appendChild(comparePrice);
    }

    info.appendChild(price);
    card.appendChild(info);
    return card;
  }

  _renderToggle(product) {
    const toggleElement = document.createElement('wishlist-toggle');
    toggleElement.className = 'wishlist-toggle wishlist-toggle--card';
    toggleElement.setAttribute('handle', product.handle);
    toggleElement.setAttribute('product-id', String(product.id));
    toggleElement.setAttribute('product-title', product.title);

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'wishlist-toggle__button';

    const label = document.createElement('span');
    label.className = 'sr-only';
    label.textContent = `Remove ${product.title} from wishlist`;
    button.appendChild(label);

    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('role', 'presentation');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('width', '18');
    svg.setAttribute('height', '18');
    svg.setAttribute('viewBox', '0 0 22 22');
    svg.setAttribute('stroke-width', '1.5');
    svg.classList.add('wishlist-toggle__icon');

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', HEART_PATH);
    path.setAttribute('fill', 'none');
    path.setAttribute('stroke', 'currentColor');
    path.setAttribute('stroke-linecap', 'round');
    path.setAttribute('stroke-linejoin', 'round');
    svg.appendChild(path);

    button.appendChild(svg);
    toggleElement.appendChild(button);
    return toggleElement;
  }
}

if (!window.customElements.get('wishlist-toggle')) window.customElements.define('wishlist-toggle', WishlistToggle);
if (!window.customElements.get('wishlist-count')) window.customElements.define('wishlist-count', WishlistCount);
if (!window.customElements.get('wishlist-grid')) window.customElements.define('wishlist-grid', WishlistGrid);

/* --- Wishlist drawer -------------------------------------------------------
   Mirrors the cart's custom shell in assets/custom.js: below 1150px the
   drawer's section is moved inside the header pill and expands downward from
   it (store-header[expanded] + height transition); from 1150px it is the same
   Nomad-style panel dropping from under the pill as the cart and search. */

const PILL_INSET = 34; // clears the phone's bottom corner radius / home bar
const PANEL_EASE = 'cubic-bezier(0.4, 0, 0.22, 1)';
const isPill = () => window.matchMedia('screen and (max-width: 1149px)').matches;
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const panelDropAnimation = (drawer, show, animate = true) => {
  const content = drawer.shadowRoot && drawer.shadowRoot.querySelector('[part="content"]');
  if (!content) return Promise.resolve();
  const overlay = drawer.shadowRoot.querySelector('[part="overlay"]');
  content.getAnimations().forEach((a) => a.cancel());
  if (overlay) {
    overlay.getAnimations().forEach((a) => a.cancel());
    const fade = overlay.animate(
      show ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }],
      { duration: 250, easing: 'ease', fill: 'both' }
    );
    if (!animate || reducedMotion()) fade.finish();
  }
  const frames = [
    { transform: 'translateY(-6%)', opacity: 0 },
    { transform: 'translateY(0)', opacity: 1 },
  ];
  if (!show) frames.reverse();
  const anim = content.animate(frames, { duration: show ? 350 : 250, easing: PANEL_EASE, fill: 'both' });
  if (!animate || reducedMotion()) anim.finish();
  // A rapid re-open cancels the in-flight animation and REJECTS .finished;
  // unsettled, that swallows dialog:after-show and the grid never renders.
  return anim.finished.catch(() => {});
};

// Move the drawer's section inside the pill wrapper (same as the cart) so the
// pill-expansion CSS can reach it.
const initWishlistInPill = () => {
  const wrapper = document.querySelector('.header-pill .header__wrapper');
  const drawer = document.getElementById('wishlist-drawer');
  const section = drawer && drawer.closest('.shopify-section');
  if (wrapper && section) wrapper.appendChild(section);
};
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWishlistInPill);
} else {
  initWishlistInPill();
}

// Opening the menu closes the wishlist (the drawer closes the others itself).
document.addEventListener('click', (event) => {
  if (!event.target.closest || !event.target.closest('button[aria-controls="header-menu"]')) return;
  const drawer = document.getElementById('wishlist-drawer');
  if (drawer && drawer.hasAttribute('open') && typeof drawer.hide === 'function') drawer.hide();
});

// Safari's bottom bar collapses/expands the visible viewport mid-scroll.
window.addEventListener('resize', () => {
  const drawer = document.getElementById('wishlist-drawer');
  if (drawer && drawer.classList.contains('bs-wishlist-open') && typeof drawer._sizeToViewport === 'function') {
    drawer._sizeToViewport();
  }
});

customElements.whenDefined('x-drawer').then(() => {
  const DrawerBase = customElements.get('x-drawer');

  class WishlistDrawer extends DrawerBase {
    // The element lives inside the header pill; the vendor's move-to-body
    // would yank it out of reach of the pill CSS.
    get shouldAppendToBody() {
      return false;
    }

    _sizeToViewport() {
      const nav = this.closest('store-header')?.querySelector('navigation-box');
      const top = nav ? nav.getBoundingClientRect().top : this.getBoundingClientRect().top;
      const available = Math.max(240, Math.round(window.innerHeight - top - PILL_INSET));
      this.style.setProperty('--bs-wishlist-h', available + 'px');
    }

    _showTransition(animate = true) {
      const storeHeader = this.closest('store-header');
      const search = storeHeader?.querySelector('search-mobile[open]');
      if (search && typeof search.hide === 'function') search.hide();
      const cart = document.getElementById('cart-drawer');
      if (cart && cart.hasAttribute('open') && typeof cart.hide === 'function') cart.hide();

      this.style.display = 'block';
      this.style.visibility = 'visible';
      this.style.opacity = '1';

      if (isPill() && storeHeader) {
        const nav = storeHeader.querySelector('navigation-box');
        if (nav && nav.open) nav.onSummaryClicked();
        // Set synchronously: bs-wishlist-open only lands two frames later, so a
        // hide inside that window must still find proof this drawer expanded
        // the pill — otherwise expanded + the blur dimmer are stranded.
        this._bsPillOpen = true;
        storeHeader.setAttribute('expanded', '');
        this._sizeToViewport();
        // The menu's page-dimmer, not the drawer's own overlay (CSS hides it
        // in pill mode — it would dim the pill itself).
        const pageOverlay = storeHeader.querySelector('div[part="overlay"]');
        if (pageOverlay) pageOverlay.removeAttribute('hidden');
        // two frames so the height transition starts from 0
        return new Promise((resolve) => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (this._bsPillOpen) this.classList.add('bs-wishlist-open');
              setTimeout(resolve, reducedMotion() || !animate ? 0 : 420);
            });
          });
        });
      }

      return panelDropAnimation(this, true, animate);
    }

    _hideTransition() {
      const storeHeader = this.closest('store-header');

      if ((this._bsPillOpen || this.classList.contains('bs-wishlist-open')) && storeHeader) {
        this._bsPillOpen = false;
        this.classList.remove('bs-wishlist-open');
        // Keep the swapped pill row during the collapse (see the cart drawer's
        // bs-cart-closing note in custom.js); custom.css keys on this class.
        this.classList.add('bs-wishlist-closing');
        const nav = storeHeader.querySelector('navigation-box');
        const cart = document.getElementById('cart-drawer');
        const cartOpen = !!(cart && cart.hasAttribute('open'));
        const searchOpen = storeHeader.querySelector('search-mobile[open]');
        if ((!nav || !nav.open) && !cartOpen && !searchOpen) {
          storeHeader.removeAttribute('expanded');
          const pageOverlay = storeHeader.querySelector('div[part="overlay"]');
          if (pageOverlay) pageOverlay.setAttribute('hidden', '');
        }
        return new Promise((resolve) => {
          setTimeout(() => {
            this.classList.remove('bs-wishlist-closing');
            this.style.opacity = '0';
            this.style.visibility = 'hidden';
            this.style.display = 'none';
            resolve();
          }, reducedMotion() ? 0 : 420);
        });
      }

      this.removeAttribute('inert');
      return panelDropAnimation(this, false).then(() => {
        this.setAttribute('inert', '');
        this.style.opacity = '0';
        this.style.visibility = 'hidden';
        this.style.display = 'none';
      });
    }
  }

  if (!window.customElements.get('wishlist-drawer')) {
    window.customElements.define('wishlist-drawer', WishlistDrawer);
  }
});

// Opening the cart closes the wishlist. custom.js replaces the cart's
// _showTransition in its own whenDefined callback; custom.js loads first in
// theme.liquid, so this wrapper composes on top of that replacement.
customElements.whenDefined('cart-drawer').then(() => {
  const CartEl = customElements.get('cart-drawer');
  const cartShow = CartEl.prototype._showTransition;
  CartEl.prototype._showTransition = function (...args) {
    const drawer = document.getElementById('wishlist-drawer');
    if (drawer && drawer.hasAttribute('open') && typeof drawer.hide === 'function') drawer.hide();
    return cartShow.apply(this, args);
  };
});
